import { useContext, useEffect, useState } from 'react';
import { DocumentReference } from 'firebase/firestore';
import { Drawer, Grid, Theme, Tooltip, useTheme } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import SignalCellularAltOutlinedIcon from '@mui/icons-material/SignalCellularAltOutlined';
import AutoAwesomeMosaicOutlinedIcon from '@mui/icons-material/AutoAwesomeMosaicOutlined';
import { PBIReport } from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';
import CompanySelect from '../../shared/input/select/CompanySelect';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import { CompanyExtended } from '../../../lib/app/company';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import {
  PBIReportData,
  PBIReportExtended,
  createPBIReport,
  deletePBIReport,
  getPBIReports,
  pbi_report_label,
  updatePBIReport
} from '../../../lib/app/powerbi/pbi_report';
import ConfigAddWidget, { CreateInput } from '../ConfigAddWidget';
import ConfigEditWidget, { EditInput } from '../ConfigEditWidget';
import { auth } from '../../../lib/google/firebase';
import ViewSelect from '../../shared/input/select/ViewSelect';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import PrimaryTabs from '../../shared/tab/PrimaryTabs';
import PrimaryTab from '../../shared/tab/PrimaryTab';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { ViewExtended, getViews } from '../../../lib/app/navigation';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

interface PBIReportGridRow extends Omit<PBIReport, 'view'> {
  company?: CompanyExtended;
  view?: ViewExtended;
}

const SOURCE = 'PanelPBIReports.tsx';

/**
 * Datagrid handling the display of configured PowerBI Reports.
 * @returns {JSX.Element}
 */
const PanelPBIReports = () => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [tab, setTab] = useState<number>(0);
  const [company, setCompany] = useState<CompanyExtended | null>(null);
  const [pbiReportRows, setPBIReportRows] = useState<Array<PBIReportGridRow>>([]);
  const [actionRow, setActionRow] = useState<PBIReportGridRow | null>(null);
  const [isCreateWidget, setIsCreateWidget] = useState<boolean>(true);
  const [displayWidgetPanelRight, setDisplayWidgetPanelRight] = useState<boolean>(false);
  const [openDialogDelete, setOpenDialogDelete] = useState<boolean>(false);
  const [gridLoading, setGridLoading] = useState<boolean>(false);
  const theme: Theme = useTheme();

  const pbi_views: Array<ViewExtended> = getViews().filter(
    (view: ViewExtended) => view.require_pbi_report
  );

  const viewInputFactory = (
    handleInputChange: (input_id: string, value: ViewExtended | null) => void
  ): JSX.Element => {
    return (
      <ViewSelect
        options={pbi_views}
        selected_options={
          actionRow
            ? pbi_views.find((view: ViewExtended) => actionRow.view?.id === view.id)
            : undefined
        }
        handleChangeViews={(view: Array<ViewExtended> | ViewExtended | null) => {
          if (!Array.isArray(view)) handleInputChange('view', view);
        }}
        group_by="module.name"
        companyId={company?.id}
      />
    );
  };

  const companyInputFactory = (
    handleInputChange: (
      input_id: string,
      value: CompanyExtended | Array<CompanyExtended> | null
    ) => void
  ): JSX.Element => {
    return (
      <CompanySelect
        handleChangeCompanies={(company: Array<CompanyExtended> | CompanyExtended | null) => {
          if (!Array.isArray(company)) handleInputChange('input_company', company);
        }}
        group_by="group.name"
      />
    );
  };

  // Handler functions
  const handleCreateClick = (): void => {
    setActionRow(null);
    setIsCreateWidget(true);
    setDisplayWidgetPanelRight(true);
  };

  const handleEditClick = (row: PBIReportGridRow): void => {
    setActionRow(row);
    setIsCreateWidget(false);
    setDisplayWidgetPanelRight(true);
  };

  const handleCloseDeleteModal = (): void => {
    setOpenDialogDelete(false);
  };

  const handleDeleteClick = (row: PBIReportGridRow): void => {
    try {
      setActionRow(row);
      setOpenDialogDelete(true);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleDeleteClick'),
          {
            row: row
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete pbi report. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const handleChangeCompany = (new_company: CompanyExtended) => {
    setCompany(new_company);
  };

  const handleUpdateRows = (rows_data: Array<PBIReportGridRow>): void => {
    setPBIReportRows(rows_data);
  };

  const handleChangeTab = (new_tab: number) => {
    setCompany(null);
    setTab(new_tab);
  };

  // Row functions
  const fetchRows = async (): Promise<void> => {
    setGridLoading(true);
    try {
      // Load rows from database.
      const pbi_reports: Array<PBIReport | PBIReportExtended> = await getPBIReports(
        company?.group.id,
        company?.id
      );
      // Map reports to views and load rows to memory.
      const pbi_report_rows: Array<PBIReportGridRow> = pbi_reports.map(
        (pbi_report: PBIReport | PBIReportExtended) => ({
          ...pbi_report,
          view: pbi_views.find((view: ViewExtended) => view.id === pbi_report.view)
        })
      );
      setPBIReportRows(pbi_report_rows);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchRows'),
          {},
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch pbi reports. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setGridLoading(false);
    }
  };

  const createRow = async (
    input_company: CompanyExtended,
    name: string,
    embed_url: string,
    dataset_id: string,
    view: ViewExtended
  ): Promise<void> => {
    try {
      const pbi_report_data: PBIReportData = {
        name: name,
        embed_url: embed_url,
        dataset_id: dataset_id,
        view: view.id,
        deleted: null
      };
      const new_pbi_report: DocumentReference = await createPBIReport(
        input_company.group.id,
        input_company.id,
        pbi_report_data,
        auth.currentUser?.email ?? ''
      );
      if (new_pbi_report) {
        handleUpdateRows([
          {
            id: new_pbi_report.id,
            ...pbi_report_data,
            view: view,
            company: input_company
          },
          ...pbiReportRows
        ]);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createRow'),
          {
            company: company,
            name: name,
            embed_url: embed_url,
            dataset_id: dataset_id,
            view: view
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to create PowerBI Report. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const updateRow = async (
    pbi_company: CompanyExtended,
    updated_row: PBIReportGridRow,
    original_data: PBIReportGridRow
  ): Promise<void> => {
    try {
      const updated_pbi_report_data: PBIReportData = {
        name: updated_row.name,
        deleted: updated_row.deleted,
        dataset_id: updated_row.dataset_id,
        embed_url: updated_row.embed_url,
        view: updated_row.view?.id ?? ''
      };
      const original_pbi_report: PBIReport = {
        ...original_data,
        view: original_data.view?.id ?? ''
      };
      await updatePBIReport(
        pbi_company.group.id,
        pbi_company.id,
        updated_row.id,
        updated_pbi_report_data,
        original_pbi_report,
        auth.currentUser?.email ?? ''
      ).then(() => {
        handleUpdateRows(
          pbiReportRows.map((row: PBIReportGridRow) =>
            updated_row.id === row.id ? { ...updated_row } : row
          )
        );
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateRow'),
          {
            updated_row: updated_row,
            original_data: original_data
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update PowerBI Report. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const deleteRow = async (
    pbi_company: CompanyExtended,
    pbi_report: PBIReportGridRow
  ): Promise<void> => {
    try {
      await deletePBIReport(
        pbi_company.group.id,
        pbi_company.id,
        pbi_report.id,
        auth.currentUser?.email ?? ''
      ).then(() => {
        setPBIReportRows(
          pbiReportRows.filter((row: PBIReportGridRow) => {
            return row.id !== pbi_report.id;
          })
        );
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteRow'),
          {
            pbi_report: pbi_report
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to remove PowerBI Report. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setOpenDialogDelete(false);
    }
  };

  useEffect(() => {
    try {
      (async () => {
        setPBIReportRows([]);
        await fetchRows();
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE),
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
    return;
  }, [company]);

  const columns: Array<GridColDef> = [
    ...(tab === 1
      ? [
          {
            field: 'company',
            headerName: 'Company',
            valueGetter: (params: GridValueGetterParams) => {
              return params.row.company ? params.row.company.name : '';
            },
            flex: 1
          }
        ]
      : []),
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'embed_url',
      headerName: 'Embed URL',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'dataset_id',
      headerName: 'Dataset ID',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'view',
      headerName: 'View',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.view?.id ?? '';
      },
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            <GridActionsCellItem
              key={1}
              icon={
                <Tooltip title="Edit PowerBI Report">
                  <EditIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Edit"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleEditClick(row)}
              disabled={row.locked}
            />
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete PowerBI Report">
                  <DeleteIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
            />
          </>
        ];
      }
    }
  ];

  const create_inputs: readonly CreateInput[] = company
    ? [
        {
          id: 'name',
          type: 'text',
          label: 'Name'
        },
        {
          id: 'embed_url',
          type: 'text',
          label: 'Embed URL',
          ignoreBannedCharacters: true
        },
        {
          id: 'dataset_id',
          type: 'text',
          label: 'Dataset ID'
        },
        {
          id: 'view',
          type: 'node',
          label: 'View',
          inputNodeFactory: viewInputFactory
        }
      ]
    : [
        {
          id: 'name',
          type: 'text',
          label: 'Name'
        },
        {
          id: 'embed_url',
          type: 'text',
          label: 'Embed URL',
          ignoreBannedCharacters: true
        },
        {
          id: 'dataset_id',
          type: 'text',
          label: 'Dataset ID'
        },
        {
          id: 'view',
          type: 'node',
          label: 'View',
          inputNodeFactory: viewInputFactory
        },
        {
          id: 'input_company',
          type: 'node',
          label: 'Company',
          inputNodeFactory: companyInputFactory
        }
      ];

  const edit_inputs: EditInput[] = [
    {
      id: 'name',
      type: 'text',
      label: 'Name'
    },
    {
      id: 'embed_url',
      type: 'text',
      label: 'Embed URL',
      ignoreBannedCharacters: true
    },
    {
      id: 'dataset_id',
      type: 'text',
      label: 'Dataset ID'
    },
    {
      id: 'view',
      type: 'node',
      label: 'View',
      inputNodeFactory: viewInputFactory
    }
  ];

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={openDialogDelete}
        allow_delete={true}
        handleCloseDeleteModal={handleCloseDeleteModal}
        delete_label={actionRow && actionRow.name}
        handleDelete={
          actionRow &&
          (async () => {
            const pbi_company: CompanyExtended | null | undefined =
              company ?? ('company' in actionRow ? actionRow.company : undefined);
            if (pbi_company) {
              await deleteRow(pbi_company, actionRow);
            }
          })
        }
        allow_archive={false}
      />

      {/* Side Widget Panel */}
      <Drawer
        anchor={'right'}
        open={displayWidgetPanelRight}
        onClose={() => setDisplayWidgetPanelRight(false)}
        PaperProps={{ style: { width: '30%', padding: '1.5rem' } }}
      >
        {/* Create / Edit Widget */}
        {isCreateWidget ? (
          <>
            <ConfigAddWidget
              widget_label={pbi_report_label.one}
              icon={
                <SignalCellularAltOutlinedIcon
                  color="primary"
                  sx={{ marginRight: '2rem' }}
                  fontSize="large"
                />
              }
              inputs={create_inputs}
              handleClose={() => setDisplayWidgetPanelRight(false)}
              createFunction={async (
                name: string,
                embed_url: string,
                dataset_id: string,
                view: ViewExtended,
                input_company: CompanyExtended
              ) => {
                const pbi_company: CompanyExtended | null = input_company ?? company;
                pbi_company && (await createRow(pbi_company, name, embed_url, dataset_id, view));
              }}
            />
          </>
        ) : (
          <ConfigEditWidget
            widget_label={pbi_report_label.one}
            icon={
              <SignalCellularAltOutlinedIcon
                color="primary"
                sx={{ marginRight: '2rem' }}
                fontSize="large"
              />
            }
            handleClose={() => setDisplayWidgetPanelRight(false)}
            edit_entity={actionRow}
            inputs={edit_inputs}
            handleEditInput={(input_id: string, value: unknown) =>
              actionRow && setActionRow({ ...actionRow, [input_id]: value })
            }
            confirmEditFunction={async (
              updated_pbi_report: PBIReportGridRow,
              original_pbi_report: PBIReportGridRow
            ) => {
              const pbi_company: CompanyExtended | null | undefined =
                company ??
                ('company' in updated_pbi_report ? updated_pbi_report.company : undefined);
              if (pbi_company) {
                await updateRow(pbi_company, updated_pbi_report, original_pbi_report);
              }
            }}
          />
        )}
      </Drawer>
      <Grid container spacing={8}>
        <Grid size={12}>
          <PrimaryTabs
            value={tab}
            onChange={(event, new_tab: number) => handleChangeTab(new_tab)}
            sx={{ width: '50%' }}
          >
            <PrimaryTab
              icon={<ApartmentOutlinedIcon />}
              iconPosition="start"
              label="Company View"
            />
            <PrimaryTab
              icon={<AutoAwesomeMosaicOutlinedIcon />}
              iconPosition="start"
              label="All PowerBI Reports"
            />
          </PrimaryTabs>
        </Grid>
        {tab === 0 && (
          <Grid size={6}>
            <CompanySelect
              group_by="group.name"
              handleChangeCompanies={(company: CompanyExtended | Array<CompanyExtended> | null) => {
                company && !Array.isArray(company) && handleChangeCompany(company);
              }}
            />
          </Grid>
        )}
        {((tab === 0 && company) || tab === 1) && (
          <Grid size={12}>
            {/* Interactive Data Table */}
            <DataGrid
              autoHeight
              rowHeight={75}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 }
                }
              }}
              hideFooter={pbiReportRows.length > 1 ? false : true}
              columnHeaderHeight={pbiReportRows.length < 1 ? 0 : 56}
              pageSizeOptions={[10, 25, 50, 100]}
              loading={gridLoading}
              rows={pbiReportRows}
              columns={columns}
              disableRowSelectionOnClick
              slots={{
                toolbar: () => (
                  <DataGridToolbar
                    entity_label={pbi_report_label}
                    disable_export={pbiReportRows.length === 0}
                    handleCreate={handleCreateClick}
                  />
                ),
                noRowsOverlay: () => <DataGridNoRows missing_label={pbi_report_label} />,
                loadingOverlay: () => <DataGridLoading columns={columns.length} />
              }}
              slotProps={{ toolbar: { handleCreate: handleCreateClick } }}
              sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default PanelPBIReports;
